/**
 * Messaging hub — Compose, Templates, History.
 *
 * - Compose: pick recipients (single phone / by class / by appt type+calendar / paste numbers)
 *            → preview merge fields → send via /api/sms/send
 * - Templates: CRUD on saved SMS bodies with merge fields like {first_name}, {class_time}
 * - History: paginated log of past sends with filter
 */
import { isoDate as melDate } from "@/lib/intl";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  MessageSquare, Send, Plus, Trash2, Save, RefreshCw, AlertTriangle, CheckCircle2,
  Bell, MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { fmtDateTime } from "@/lib/intl";
import {
  Dialog as _EmailDialog,
  DialogContent as _EmailDialogContent,
  DialogHeader as _EmailDialogHeader,
  DialogTitle as _EmailDialogTitle,
} from "@/components/ui/dialog";
import SmsQuickSendDialog from "@/components/SmsQuickSendDialog";
import ConversationDialog from "@/components/ConversationDialog";

const MERGE_HINTS = ["{first_name}", "{name}", "{class_time}", "{class_type}"];

function applyMerge(body, fields) {
  return (body || "").replace(/\{([a-zA-Z0-9_]+)\}/g, (_, k) =>
    fields?.[k] == null ? "" : String(fields[k]),
  );
}

function classTimeStr(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return melDate(d);
}

export default function Messaging() {
  return (
    <div className="space-y-6" data-testid="messaging-page">
      <div>
        <div className="text-2xs uppercase tracking-[0.2em] text-zinc-500">Communication</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950 mt-1">SMS Messaging</h1>
        <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
          Send transactional SMS to a single client, an entire class, or a bulk list of customers — backed by
          ClickSend. Templates with merge fields keep messages personal at scale.
        </p>
      </div>

      <Tabs defaultValue="compose">
        <TabsList>
          <TabsTrigger value="compose" data-testid="tab-compose">Compose</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">Reminders</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox">Inbox</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">SMS history</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">Email log</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-4">
          <ComposeTab />
        </TabsContent>
        <TabsContent value="reminders" className="mt-4">
          <RemindersTab />
        </TabsContent>
        <TabsContent value="inbox" className="mt-4">
          <InboxTab />
        </TabsContent>
        <TabsContent value="templates" className="mt-4">
          <TemplatesTab />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <HistoryTab />
        </TabsContent>
        <TabsContent value="emails" className="mt-4">
          <EmailLogTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// COMPOSE
// ============================================================================
function ComposeTab() {
  const [mode, setMode] = useState("single"); // single | class | bulk
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");

  // Single
  const [singlePhone, setSinglePhone] = useState("");
  const [singleName, setSingleName] = useState("");

  // Class
  const [calendars, setCalendars] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [calId, setCalId] = useState("");
  const [aptId, setAptId] = useState("");
  const [minDate, setMinDate] = useState(todayISO(0));
  const [maxDate, setMaxDate] = useState(todayISO(7));
  const [classRecipients, setClassRecipients] = useState([]);
  const [loadingClass, setLoadingClass] = useState(false);

  // Bulk
  const [bulkText, setBulkText] = useState("");

  // Send
  const [recipientChecks, setRecipientChecks] = useState({});
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/sms/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    api.get("/calendars").then((r) => setCalendars(r.data || [])).catch(() => {});
    api.get("/appointment-types").then((r) => setApptTypes(r.data || [])).catch(() => {});
  }, []);

  function applyTemplate(tid) {
    setSelectedTpl(tid);
    const t = templates.find((x) => x.id === tid);
    if (t) setBody(t.body);
  }

  async function loadClassRecipients() {
    if (!calId && !aptId) {
      toast.error("Pick a calendar or appointment type");
      return;
    }
    setLoadingClass(true);
    setResults(null);
    try {
      const payload = {
        min_date: minDate, max_date: maxDate,
        calendar_id: calId ? Number(calId) : null,
        appointment_type_id: aptId ? Number(aptId) : null,
      };
      const r = await api.post("/sms/recipients/by-class", payload);
      const list = r.data || [];
      setClassRecipients(list);
      const init = {};
      list.forEach((rec) => { if (rec.valid && rec.to) init[rec.to] = true; });
      setRecipientChecks(init);
      toast.success(`Found ${list.length} attendee(s)`, {
        description: `${list.filter((x) => x.valid).length} with valid phones`,
      });
    } catch (e) {
      toast.error("Could not load recipients", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoadingClass(false);
    }
  }

  const activeRecipients = useMemo(() => {
    if (mode === "single") {
      return singlePhone.trim() ? [{
        to: singlePhone.trim(),
        raw_phone: singlePhone.trim(),
        name: singleName.trim() || null,
        first_name: (singleName.trim().split(" ")[0]) || null,
        valid: true,
      }] : [];
    }
    if (mode === "class") return classRecipients;
    if (mode === "bulk") {
      // Each non-empty line: "phone, name" or "phone"
      return bulkText.split(/\r?\n/)
        .map((l) => l.trim()).filter(Boolean)
        .map((l) => {
          const [phone, ...rest] = l.split(",").map((x) => x.trim());
          const name = rest.join(", ");
          return {
            to: phone,
            raw_phone: phone,
            name: name || null,
            first_name: name ? name.split(" ")[0] : null,
            valid: !!phone,
          };
        });
    }
    return [];
  }, [mode, singlePhone, singleName, classRecipients, bulkText]);

  // For class/bulk modes, recipient_checks gate the actual send list.
  const selected = useMemo(() => {
    if (mode === "single") return activeRecipients;
    return activeRecipients.filter((r) => r.to && recipientChecks[r.to]);
  }, [activeRecipients, recipientChecks, mode]);

  function toggleAll(check) {
    const next = {};
    activeRecipients.forEach((r) => { if (r.to) next[r.to] = check; });
    setRecipientChecks(next);
  }

  async function send() {
    if (!body.trim()) {
      toast.error("Message body is empty");
      return;
    }
    if (selected.length === 0) {
      toast.error("Pick at least one recipient");
      return;
    }
    setSending(true);
    try {
      const messages = selected.map((r) => ({
        to: r.to,
        body: applyMerge(body, {
          first_name: r.first_name || (r.name || "").split(" ")[0] || "",
          name: r.name || "",
          class_time: classTimeStr(r.appointment_datetime),
          class_type: r.appointment_type || "",
        }),
        recipient_name: r.name,
        appointment_id: r.appointment_id,
      }));
      const r = await api.post("/sms/send", { messages, source: mode === "class" ? "schedule_class" : mode === "bulk" ? "bulk_customers" : "compose" });
      setResults(r.data);
      const sent = r.data?.sent_count ?? 0;
      const failed = r.data?.failed_count ?? 0;
      if (failed === 0) {
        toast.success(`Sent ${sent} SMS`, {
          description: r.data?.total_price != null ? `Cost: $${r.data.total_price.toFixed(2)}` : undefined,
        });
      } else {
        toast.warning(`Sent ${sent}, failed ${failed}`);
      }
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left — recipient picker */}
      <Card className="p-5 rounded-lg border border-zinc-200 shadow-none lg:col-span-1" data-testid="compose-recipients">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">Recipients</div>

        <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden text-xs">
          {[
            { v: "single", l: "Single" },
            { v: "class", l: "By class" },
            { v: "bulk", l: "Bulk" },
          ].map((b, i) => (
            <button
              key={b.v}
              type="button"
              onClick={() => { setMode(b.v); setResults(null); }}
              className={
                "px-3 py-1.5 " + (mode === b.v ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100")
                + (i > 0 ? " border-l border-zinc-300" : "")
              }
              data-testid={`compose-mode-${b.v}`}
            >
              {b.l}
            </button>
          ))}
        </div>

        {mode === "single" && (
          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Phone (E.164)</Label>
              <Input value={singlePhone} onChange={(e) => setSinglePhone(e.target.value)}
                placeholder="+614…  or  04XX XXX XXX"
                className="border-zinc-300 mt-1.5 font-mono-num" data-testid="single-phone-input" />
            </div>
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Name (optional)</Label>
              <Input value={singleName} onChange={(e) => setSingleName(e.target.value)}
                className="border-zinc-300 mt-1.5" data-testid="single-name-input" />
            </div>
          </div>
        )}

        {mode === "class" && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">From</Label>
                <Input type="date" value={minDate} onChange={(e) => setMinDate(e.target.value)}
                  className="border-zinc-300 mt-1.5 font-mono-num" data-testid="class-min-date" />
              </div>
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">To</Label>
                <Input type="date" value={maxDate} onChange={(e) => setMaxDate(e.target.value)}
                  className="border-zinc-300 mt-1.5 font-mono-num" data-testid="class-max-date" />
              </div>
            </div>
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Calendar</Label>
              <Select value={calId} onValueChange={(v) => setCalId(v === "_" ? "" : v)}>
                <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="class-calendar-select">
                  <SelectValue placeholder="Any calendar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_">Any calendar</SelectItem>
                  {calendars.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Appointment type</Label>
              <Select value={aptId} onValueChange={(v) => setAptId(v === "_" ? "" : v)}>
                <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="class-type-select">
                  <SelectValue placeholder="Any type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_">Any type</SelectItem>
                  {apptTypes.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={loadClassRecipients} disabled={loadingClass}
              variant="outline" className="border-zinc-300 w-full" data-testid="class-load-btn">
              <RefreshCw className={"h-4 w-4 mr-1.5 " + (loadingClass ? "animate-spin" : "")} />
              {loadingClass ? "Loading…" : "Load attendees"}
            </Button>
          </div>
        )}

        {mode === "bulk" && (
          <div className="mt-4 space-y-2">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              One per line: <span className="font-mono-num">phone, name</span> (name optional)
            </Label>
            <Textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)}
              rows={8} placeholder={"+61411222333, Jane Smith\n0412555666\n+61477888999, Tom"}
              className="border-zinc-300 font-mono-num text-sm" data-testid="bulk-textarea" />
            <p className="text-2xs text-zinc-500">{activeRecipients.length} numbers detected</p>
          </div>
        )}

        {/* Recipient list (class + bulk) */}
        {(mode === "class" || mode === "bulk") && activeRecipients.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-2xs text-zinc-500 mb-1">
              <span>{selected.length} of {activeRecipients.length} selected</span>
              <div className="flex gap-2">
                <button className="hover:underline" onClick={() => toggleAll(true)}>All</button>
                <button className="hover:underline" onClick={() => toggleAll(false)}>None</button>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto rounded-md border border-zinc-200 divide-y divide-zinc-100">
              {activeRecipients.map((r, i) => (
                <div key={(r.to || "x") + i} className="p-2 flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!!recipientChecks[r.to]}
                    disabled={!r.to}
                    onCheckedChange={(v) => setRecipientChecks((c) => ({ ...c, [r.to]: !!v }))}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-900 truncate">{r.name || "—"}</div>
                    <div className="font-mono-num text-2xs text-zinc-500 truncate">
                      {r.to || r.raw_phone || "(no phone)"}
                      {r.appointment_datetime && ` · ${classTimeStr(r.appointment_datetime)}`}
                      {r.appointment_type && ` · ${r.appointment_type}`}
                    </div>
                  </div>
                  {!r.to && (
                    <span className="inline-flex items-center gap-1 text-2xs text-amber-700">
                      <AlertTriangle className="h-3 w-3" />invalid
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Right — message + send */}
      <Card className="p-5 rounded-lg border border-zinc-200 shadow-none lg:col-span-2" data-testid="compose-message">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">Message</div>

        {templates.length > 0 && (
          <div className="mb-3">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Template (optional)</Label>
            <Select value={selectedTpl} onValueChange={applyTemplate}>
              <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="compose-template-select">
                <SelectValue placeholder="Pick a saved template…" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Label className="text-2xs uppercase tracking-wider text-zinc-500">Body</Label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
          className="border-zinc-300 mt-1.5 font-mono-num text-sm"
          placeholder="Hi {first_name}, just a reminder about {class_type} at {class_time}."
          data-testid="compose-body-textarea" />
        <div className="mt-1 flex flex-wrap items-center gap-1 text-2xs">
          <span className="text-zinc-500">Merge fields:</span>
          {MERGE_HINTS.map((h) => (
            <button key={h} type="button" onClick={() => setBody((b) => b + h)}
              className="font-mono-num text-zinc-700 hover:text-zinc-950 hover:underline">{h}</button>
          ))}
          <span className="ml-auto font-mono-num text-zinc-500">
            {body.length} chars · ~{Math.max(1, Math.ceil(body.length / 160))} part(s)
          </span>
        </div>

        {/* Preview of first message after merge */}
        {selected[0] && (
          <div className="mt-3 p-3 rounded-md border border-zinc-200 bg-zinc-50 text-sm">
            <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">Preview (first recipient)</div>
            <div className="font-mono-num text-zinc-900 whitespace-pre-wrap">
              {applyMerge(body, {
                first_name: selected[0].first_name || (selected[0].name || "").split(" ")[0] || "",
                name: selected[0].name || "",
                class_time: classTimeStr(selected[0].appointment_datetime),
                class_type: selected[0].appointment_type || "",
              }) || <span className="text-zinc-400">(empty)</span>}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button onClick={send} disabled={sending || selected.length === 0 || !body.trim()}
            className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="compose-send-btn">
            <Send className="h-4 w-4 mr-1.5" />
            {sending ? "Sending…" : `Send to ${selected.length}`}
          </Button>
          <span className="text-2xs text-zinc-500">
            {selected.length} recipient(s) · ~{Math.max(1, Math.ceil(body.length / 160))} SMS part(s) each
          </span>
        </div>

        {results && (
          <div className="mt-4 p-3 rounded-md border border-zinc-200">
            <div className="text-sm mb-2">
              Sent <span className="font-semibold">{results.sent_count}</span> ·
              Failed <span className="font-semibold">{results.failed_count}</span>
              {results.total_price != null && <> · Cost <span className="font-mono-num">${results.total_price.toFixed(2)}</span></>}
              {results.balance != null && <> · Balance <span className="font-mono-num">${results.balance.toFixed(2)}</span></>}
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-zinc-100">
              {(results.results || []).map((m, i) => {
                const ok = (m.status || "").toUpperCase() === "SUCCESS";
                return (
                  <div key={i} className="py-1.5 flex items-center gap-2 text-sm">
                    {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                    <span className="font-mono-num">{m.to}</span>
                    <span className={"ml-auto text-xs " + (ok ? "text-emerald-700" : "text-amber-700")}>
                      {m.status || "—"}{m.error_text ? ` · ${m.error_text}` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// TEMPLATES
// ============================================================================
function TemplatesTab() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const r = await api.get("/sms/templates");
      setList(r.data || []);
    } catch (e) { /* */ }
  }
  useEffect(() => { refresh(); }, []);

  async function save() {
    if (!name.trim() || !body.trim()) {
      toast.error("Both name and body are required");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/sms/templates/${editingId}`, { name, body });
        toast.success("Template updated");
      } else {
        await api.post("/sms/templates", { name, body });
        toast.success("Template saved");
      }
      setName(""); setBody(""); setEditingId(null);
      await refresh();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (!confirm("Delete this template?")) return;
    try {
      await api.delete(`/sms/templates/${id}`);
      toast.success("Deleted");
      await refresh();
      if (editingId === id) { setEditingId(null); setName(""); setBody(""); }
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  function edit(t) {
    setEditingId(t.id);
    setName(t.name);
    setBody(t.body);
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-5 rounded-lg border border-zinc-200 shadow-none lg:col-span-1" data-testid="templates-editor">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">
          {editingId ? "Edit template" : "New template"}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              className="border-zinc-300 mt-1.5" placeholder="Class cancelled" data-testid="template-name-input" />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Body</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
              className="border-zinc-300 mt-1.5 font-mono-num text-sm"
              placeholder="Hi {first_name}, your {class_type} at {class_time} is cancelled. Sorry!"
              data-testid="template-body-input" />
            <div className="mt-1 flex flex-wrap gap-1 text-2xs">
              <span className="text-zinc-500">Merge:</span>
              {MERGE_HINTS.map((h) => (
                <button key={h} type="button" onClick={() => setBody((b) => b + h)}
                  className="font-mono-num text-zinc-700 hover:text-zinc-950 hover:underline">{h}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="template-save-btn">
              <Save className="h-4 w-4 mr-1.5" />{busy ? "Saving…" : editingId ? "Update" : "Save"}
            </Button>
            {editingId && (
              <Button variant="outline" className="border-zinc-300" onClick={() => { setEditingId(null); setName(""); setBody(""); }}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-5 rounded-lg border border-zinc-200 shadow-none lg:col-span-2" data-testid="templates-list">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">Saved templates</div>
        {list.length === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center">
            <Plus className="h-6 w-6 mx-auto mb-2 text-zinc-300" />No templates yet — create one on the left.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {list.map((t) => (
              <div key={t.id} className="py-3 flex items-start gap-3" data-testid={`template-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-900">{t.name}</div>
                  <div className="text-xs text-zinc-600 font-mono-num truncate whitespace-pre-wrap line-clamp-2 max-w-prose">
                    {t.body}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="border-zinc-300" onClick={() => edit(t)}>Edit</Button>
                <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => remove(t.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// HISTORY
// ============================================================================
function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 50;

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/sms/log", { params: { q: q || undefined, skip, limit } });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error("Could not load history", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip]);

  return (
    <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden" data-testid="sms-history">
      <div className="p-4 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by phone, name or body…"
          className="border-zinc-300 max-w-xs font-mono-num" data-testid="history-search" />
        <Button onClick={() => { setSkip(0); load(); }} variant="outline" className="border-zinc-300" data-testid="history-refresh">
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (loading ? "animate-spin" : "")} />Search
        </Button>
        <span className="ml-auto text-2xs text-zinc-500 font-mono-num">{total} total</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="text-xs font-semibold uppercase text-zinc-600">Sent</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-zinc-600">To</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-zinc-600">Name</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-zinc-600">Body</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-zinc-600">Status</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-zinc-600 text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-zinc-500">No messages yet.</TableCell></TableRow>
            ) : rows.map((r, i) => {
              const ok = (r.status || "").toUpperCase() === "SUCCESS";
              return (
                <TableRow key={i}>
                  <TableCell className="text-xs font-mono-num">{fmtDateTime(r.sent_at)}</TableCell>
                  <TableCell className="text-xs font-mono-num">{r.to}</TableCell>
                  <TableCell className="text-sm">{r.recipient_name || "—"}</TableCell>
                  <TableCell className="text-xs max-w-md truncate" title={r.body}>{r.body}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={ok ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>
                      {r.status || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono-num text-right">
                    {r.price != null ? `$${Number(r.price).toFixed(3)}` : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="p-3 flex items-center justify-between border-t border-zinc-200 text-xs">
        <Button variant="outline" className="border-zinc-300" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Prev</Button>
        <span className="text-zinc-500 font-mono-num">{skip + 1}–{Math.min(skip + rows.length, total)} of {total}</span>
        <Button variant="outline" className="border-zinc-300" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}>Next</Button>
      </div>
    </Card>
  );
}


// ============================================================================
// REMINDERS — list upcoming bookings with per-row toggle + customer pref
// ============================================================================
function RemindersTab() {
  const [minDate, setMinDate] = useState(todayISO(0));
  const [maxDate, setMaxDate] = useState(todayISO(7));
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(null);   // "24h"|"2h"|null

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/reminders/upcoming", {
        params: { min_date: minDate, max_date: maxDate },
      });
      setRows(r.data?.rows || []);
      setCount(r.data?.count || 0);
    } catch (e) {
      toast.error("Could not load upcoming", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function toggleOverride(row, nextOn) {
    // If the row's effective state already matches what user wants AND the
    // current reason is type-level, sending null clears any per-appt override.
    // Otherwise we explicitly set true/false.
    try {
      await api.post("/reminders/override", {
        appointment_id: row.id,
        enabled: nextOn,
      });
      await load();
    } catch (e) {
      toast.error("Override failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function clearOverride(row) {
    try {
      await api.post("/reminders/override", { appointment_id: row.id, enabled: null });
      await load();
      toast.success("Override cleared — using type/customer rule");
    } catch (e) {
      toast.error("Could not clear override", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function setCustomerPref(row, pref) {
    try {
      await api.post("/reminders/customer-pref", {
        email: row.email, name: [row.first_name, row.last_name].filter(Boolean).join(" "), pref,
      });
      await load();
      toast.success(`Customer reminders set to "${pref}"`);
    } catch (e) {
      toast.error("Could not update preference", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function runNow(slot) {
    setRunning(slot);
    try {
      const r = await api.post(`/reminders/run-now?slot=${slot}&force=true`);
      const d = r.data || {};
      toast.success(`Run-now (${slot}): sent ${d.sent || 0} / due ${d.total_due || 0}`,
        { description: d.failed ? `${d.failed} failed` : undefined });
      await load();
    } catch (e) {
      toast.error("Run-now failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(null);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 rounded-lg border border-zinc-200 shadow-none" data-testid="reminders-controls">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">From</Label>
            <Input type="date" value={minDate} onChange={(e) => setMinDate(e.target.value)}
              className="border-zinc-300 mt-1.5 font-mono-num" />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">To</Label>
            <Input type="date" value={maxDate} onChange={(e) => setMaxDate(e.target.value)}
              className="border-zinc-300 mt-1.5 font-mono-num" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="reminders-load-btn">
            <RefreshCw className={"h-4 w-4 mr-1.5 " + (loading ? "animate-spin" : "")} />
            {loading ? "Loading…" : "Reload"}
          </Button>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" className="border-zinc-300" onClick={() => runNow("48h")} disabled={running === "48h"} data-testid="reminders-run-48h">
              <Bell className="h-4 w-4 mr-1.5" />Run 48h now
            </Button>
            <Button variant="outline" className="border-zinc-300" onClick={() => runNow("24h")} disabled={running === "24h"} data-testid="reminders-run-24h">
              <Bell className="h-4 w-4 mr-1.5" />Run 24h now
            </Button>
            <Button variant="outline" className="border-zinc-300" onClick={() => runNow("2h")} disabled={running === "2h"} data-testid="reminders-run-2h">
              <Bell className="h-4 w-4 mr-1.5" />Run 2h now
            </Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden" data-testid="reminders-table">
        <div className="p-4 border-b border-zinc-200 flex items-center text-xs text-zinc-500">
          <span className="font-mono-num">{count}</span>
          <span className="ml-1">upcoming · "Override" forces this booking on/off regardless of type / customer rule</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                <TableHead className="text-xs uppercase">When</TableHead>
                <TableHead className="text-xs uppercase">Client</TableHead>
                <TableHead className="text-xs uppercase">Phone</TableHead>
                <TableHead className="text-xs uppercase">Type</TableHead>
                <TableHead className="text-xs uppercase">Reminder</TableHead>
                <TableHead className="text-xs uppercase">Customer pref</TableHead>
                <TableHead className="text-xs uppercase">Sent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-zinc-500">No upcoming appointments in this range.</TableCell></TableRow>
              ) : rows.map((r) => {
                const overridden = r.override !== null && r.override !== undefined;
                return (
                  <TableRow key={r.id} data-testid={`reminder-row-${r.id}`}>
                    <TableCell className="text-xs font-mono-num">{fmtDateTime(r.datetime)}</TableCell>
                    <TableCell className="text-sm">
                      {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
                      {r.email && <div className="text-2xs text-zinc-500 truncate">{r.email}</div>}
                    </TableCell>
                    <TableCell className="text-xs font-mono-num">
                      {r.phone_normalized || <span className="text-amber-700">no phone</span>}
                    </TableCell>
                    <TableCell className="text-xs">{r.type}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!!r.reminder_on}
                          onCheckedChange={(v) => toggleOverride(r, !!v)}
                          data-testid={`reminder-toggle-${r.id}`}
                        />
                        <Badge
                          variant="outline"
                          className={overridden ? "border-blue-300 text-blue-700" : "border-zinc-300 text-zinc-600"}
                          title={r.reminder_reason}
                        >
                          {overridden ? "override" : r.reminder_reason}
                        </Badge>
                        {overridden && (
                          <button
                            type="button"
                            onClick={() => clearOverride(r)}
                            className="text-2xs text-zinc-500 hover:text-zinc-900 hover:underline"
                          >
                            clear
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select value={r.customer_pref || "default"} onValueChange={(v) => setCustomerPref(r, v)}>
                        <SelectTrigger className="h-8 text-xs border-zinc-300 w-32" data-testid={`reminder-pref-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Default</SelectItem>
                          <SelectItem value="always">Always</SelectItem>
                          <SelectItem value="never">Never</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs font-mono-num">
                      <div className="flex gap-1.5">
                        {r.sent_48h && <Badge variant="outline" className="border-emerald-300 text-emerald-700">48h</Badge>}
                        {r.sent_24h && <Badge variant="outline" className="border-emerald-300 text-emerald-700">24h</Badge>}
                        {r.sent_2h && <Badge variant="outline" className="border-emerald-300 text-emerald-700">2h</Badge>}
                        {!r.sent_48h && !r.sent_24h && !r.sent_2h && <span className="text-zinc-400">—</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}


// ============================================================================
// INBOX — inbound SMS replies with per-row Reply button
// ============================================================================
const INTENT_STYLE = {
  confirm:      { label: "Confirmed",   cls: "border-emerald-300 text-emerald-700" },
  cancel:       { label: "Cancel",      cls: "border-amber-300 text-amber-700" },
  reschedule:   { label: "Reschedule",  cls: "border-blue-300 text-blue-700" },
  opt_out:      { label: "Opt-out",     cls: "border-red-300 text-red-700" },
  opt_back_in:  { label: "Opt-in",      cls: "border-emerald-300 text-emerald-700" },
  unknown:      { label: "Reply",       cls: "border-zinc-300 text-zinc-700" },
  ignored:      { label: "Ignored",     cls: "border-zinc-300 text-zinc-500" },
};

function InboxTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [convTarget, setConvTarget] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/sms/inbound", { params: { limit: 100 } });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error("Could not load inbox", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function openReply(row) {
    setReplyTarget({
      to: row.from_phone,
      raw_phone: row.from_phone,
      name: row.recipient_name || null,
      first_name: (row.recipient_name || "").split(" ")[0] || null,
      valid: true,
      _originalBody: row.body,
      _originalIntent: row.intent,
    });
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 rounded-lg border border-zinc-200 shadow-none flex items-center gap-3" data-testid="inbox-controls">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Inbound SMS · {rows.length}</div>
        <div className="text-2xs text-zinc-500">
          Every reply that ClickSend forwards to our webhook lands here. Click <b>Reply</b> to respond
          to the client directly.
        </div>
        <Button onClick={load} disabled={loading} variant="outline" className="border-zinc-300 ml-auto" data-testid="inbox-refresh">
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (loading ? "animate-spin" : "")} />
          Reload
        </Button>
      </Card>

      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden" data-testid="inbox-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                <TableHead className="text-xs uppercase">Received</TableHead>
                <TableHead className="text-xs uppercase">Name</TableHead>
                <TableHead className="text-xs uppercase">From</TableHead>
                <TableHead className="text-xs uppercase">Reply</TableHead>
                <TableHead className="text-xs uppercase">Intent</TableHead>
                <TableHead className="text-xs uppercase">Action taken</TableHead>
                <TableHead className="text-xs uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-zinc-500">No inbound messages yet.</TableCell></TableRow>
              ) : rows.map((r) => {
                const style = INTENT_STYLE[r.intent] || INTENT_STYLE.unknown;
                return (
                  <TableRow key={r._id} data-testid={`inbox-row-${r._id}`}>
                    <TableCell className="text-xs font-mono-num whitespace-nowrap">{fmtDateTime(r.received_at)}</TableCell>
                    <TableCell className="text-sm">
                      {r.recipient_name || <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="text-xs font-mono-num">{r.from_phone}</TableCell>
                    <TableCell className="text-sm max-w-md">
                      <div className="whitespace-pre-wrap">{r.body}</div>
                      {r.matched_reminder_slot && (
                        <div className="text-2xs text-zinc-500 mt-0.5">
                          ↳ matched {r.matched_reminder_slot} reminder for appt #{r.matched_appointment_id}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={style.cls}>{style.label}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-zinc-600 max-w-xs">
                      {r.action || "—"}
                      {r.cancelled && <span className="ml-1 text-emerald-700">· cancelled ✓</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1.5 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-300 h-8 px-2"
                          onClick={() => setConvTarget({ phone: r.from_phone, name: r.recipient_name })}
                          data-testid={`inbox-view-${r._id}`}
                          title="View full conversation"
                        >
                          Thread
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-300 h-8 px-2"
                          onClick={() => openReply(r)}
                          data-testid={`inbox-reply-${r._id}`}
                        >
                          <Send className="h-3.5 w-3.5 mr-1" /> Reply
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <SmsQuickSendDialog
        open={!!replyTarget}
        onClose={() => { setReplyTarget(null); load(); }}
        title={replyTarget
          ? `Reply to ${replyTarget.to}${replyTarget._originalBody ? ` — "${replyTarget._originalBody.slice(0, 40)}${replyTarget._originalBody.length > 40 ? "…" : ""}"` : ""}`
          : "Reply"}
        source="inbox_reply"
        recipients={replyTarget ? [replyTarget] : []}
      />

      <ConversationDialog
        open={!!convTarget}
        onClose={() => { setConvTarget(null); load(); }}
        phone={convTarget?.phone}
        recipientName={convTarget?.name}
      />
    </div>
  );
}

// ============================================================================
// EMAIL LOG — every outgoing email; click to see the exact body sent
// ============================================================================
const EMAIL_SOURCE_STYLE = {
  milestone: "border-blue-300 text-blue-700",
  glad: "border-emerald-300 text-emerald-700",
  sms_reply_admin: "border-amber-300 text-amber-700",
  schedule: "border-zinc-300 text-zinc-700",
  receipt: "border-purple-300 text-purple-700",
};

function EmailLogTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewingDetail, setViewingDetail] = useState(null);
  const [resending, setResending] = useState(null);   // id or null
  const [resendTarget, setResendTarget] = useState(null); // {row, to}
  const limit = 50;

  async function load() {
    setLoading(true);
    try {
      const params = { limit, skip };
      if (q) params.q = q;
      if (source) params.source = source;
      const r = await api.get("/emails/log", { params });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error("Could not load email log", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip]);

  async function openEmail(row) {
    setViewing(row);
    setViewingDetail(null);
    try {
      const r = await api.get(`/emails/log/${row._id}`);
      setViewingDetail(r.data);
    } catch (e) {
      toast.error("Could not load email body", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function resendEmail(row, opts = {}) {
    // If the caller passed an override To, send immediately. Otherwise open
    // the redirect dialog so the user can accept the original recipient or
    // type a different one.
    if (!opts.to) {
      setResendTarget({ row: opts.viaDialog && viewing ? viewing : row, to: (opts.viaDialog && viewing ? viewing.to : row.to) });
      return;
    }
    setResending(row._id);
    try {
      const r = await api.post(`/emails/log/${row._id}/resend`,
        opts.to && opts.to !== row.to ? { to: opts.to } : {});
      if (r.data?.ok) {
        toast.success(`Sent to ${r.data.sent_to || opts.to}`);
      } else {
        toast.error("Resend failed", { description: r.data?.error || "See email log for details" });
      }
      setResendTarget(null);
      setSkip(0);
      await load();
    } catch (e) {
      toast.error("Resend failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setResending(null);
    }
  }

  return (
    <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden" data-testid="email-log">
      <div className="p-4 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by recipient, subject or body…"
          className="border-zinc-300 max-w-xs font-mono-num" data-testid="email-log-search" />
        <select value={source} onChange={(e) => setSource(e.target.value)}
          className="text-sm border border-zinc-300 rounded-md px-2 h-9"
          data-testid="email-log-source">
          <option value="">All sources</option>
          <option value="milestone">Milestone</option>
          <option value="glad">GLA:D</option>
          <option value="sms_reply_admin">SMS reply admin notice</option>
          <option value="schedule">Daily schedule</option>
          <option value="receipt">Tax receipt</option>
        </select>
        <Button onClick={() => { setSkip(0); load(); }} variant="outline" className="border-zinc-300" data-testid="email-log-refresh">
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (loading ? "animate-spin" : "")} />Search
        </Button>
        <span className="ml-auto text-2xs text-zinc-500 font-mono-num">{total} total</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="text-xs uppercase">Sent</TableHead>
              <TableHead className="text-xs uppercase">To</TableHead>
              <TableHead className="text-xs uppercase">Subject</TableHead>
              <TableHead className="text-xs uppercase">Source</TableHead>
              <TableHead className="text-xs uppercase">Status</TableHead>
              <TableHead className="text-xs uppercase text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-zinc-500">
                {loading ? "Loading…" : "No emails logged yet."}
              </TableCell></TableRow>
            ) : rows.map((r) => {
              const cls = EMAIL_SOURCE_STYLE[r.source] || "border-zinc-300 text-zinc-700";
              const ok = (r.status || "").toUpperCase() === "SUCCESS";
              return (
                <TableRow key={r._id} data-testid={`email-log-row-${r._id}`}>
                  <TableCell className="text-xs font-mono-num whitespace-nowrap">{fmtDateTime(r.sent_at)}</TableCell>
                  <TableCell className="text-xs font-mono-num">{r.to}</TableCell>
                  <TableCell className="text-sm max-w-md truncate" title={r.subject}>{r.subject}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cls + " text-2xs"}>{r.source_label || r.source}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={ok ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>
                      {r.status || "—"}
                    </Badge>
                    {r.error && (
                      <div className="text-2xs text-amber-700 mt-0.5 truncate max-w-[220px]" title={r.error}>{r.error}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1.5 justify-end">
                      <Button size="sm" variant="outline" className="border-zinc-300 h-8 px-2"
                        onClick={() => openEmail(r)} data-testid={`email-log-view-${r._id}`}>
                        View
                      </Button>
                      <Button size="sm" variant="outline" className="border-zinc-300 h-8 px-2"
                        disabled={resending === r._id}
                        onClick={() => resendEmail(r)}
                        data-testid={`email-log-resend-${r._id}`}
                        title="Resend this exact email to the same recipient">
                        <Send className={"h-3.5 w-3.5 " + (resending === r._id ? "animate-spin" : "")} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="p-3 flex items-center justify-between border-t border-zinc-200 text-xs">
        <Button variant="outline" className="border-zinc-300" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Prev</Button>
        <span className="text-zinc-500 font-mono-num">{skip + 1}–{Math.min(skip + rows.length, total)} of {total}</span>
        <Button variant="outline" className="border-zinc-300" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}>Next</Button>
      </div>

      {/* View-email dialog */}
      <_EmailDialog open={!!viewing} onOpenChange={(o) => !o && (setViewing(null), setViewingDetail(null))}>
        <_EmailDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-log-dialog">
          <_EmailDialogHeader>
            <_EmailDialogTitle className="truncate">{viewing?.subject || "Email"}</_EmailDialogTitle>
          </_EmailDialogHeader>
          {viewing && (
            <div className="space-y-2">
              <div className="text-xs text-zinc-600 space-y-0.5 font-mono-num">
                <div><b className="text-zinc-500 font-sans">To:</b> {viewing.to}</div>
                <div><b className="text-zinc-500 font-sans">Sent:</b> {fmtDateTime(viewing.sent_at)}</div>
                <div><b className="text-zinc-500 font-sans">Source:</b> {viewing.source_label || viewing.source}</div>
                <div><b className="text-zinc-500 font-sans">From:</b> {viewing.sender_email} ({viewing.brand_name})</div>
                <div><b className="text-zinc-500 font-sans">Status:</b> {viewing.status}</div>
                {viewing.attachment_filename && (
                  <div><b className="text-zinc-500 font-sans">Attachment:</b> {viewing.attachment_filename}</div>
                )}
              </div>
              <div className="pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-zinc-300"
                  disabled={resending === viewing._id}
                  onClick={() => resendEmail(viewing, { viaDialog: true })}
                  data-testid="email-log-dialog-resend"
                >
                  <Send className={"h-3.5 w-3.5 mr-1.5 " + (resending === viewing._id ? "animate-spin" : "")} />
                  Resend this email
                </Button>
              </div>
              <div className="mt-3 rounded-md border border-zinc-200">
                <div className="text-2xs uppercase tracking-wider text-zinc-500 p-2 border-b border-zinc-200 bg-zinc-50">
                  Rendered body
                </div>
                <div className="p-4 bg-white text-sm"
                  /* eslint-disable-next-line react/no-danger */
                  dangerouslySetInnerHTML={{ __html: viewingDetail?.html || "<p style='color:#a1a1aa'>Loading…</p>" }} />
              </div>
            </div>
          )}
        </_EmailDialogContent>
      </_EmailDialog>

      {/* Resend-with-different-address dialog */}
      <_EmailDialog open={!!resendTarget} onOpenChange={(o) => !o && setResendTarget(null)}>
        <_EmailDialogContent className="max-w-md" data-testid="email-resend-dialog">
          <_EmailDialogHeader>
            <_EmailDialogTitle>Resend email</_EmailDialogTitle>
          </_EmailDialogHeader>
          {resendTarget && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-600">
                <div className="truncate"><b className="text-zinc-500">Subject:</b> {resendTarget.row.subject}</div>
                <div><b className="text-zinc-500">Originally sent to:</b> <span className="font-mono-num">{resendTarget.row.to}</span></div>
              </div>
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Send to</Label>
                <Input
                  value={resendTarget.to}
                  onChange={(e) => setResendTarget({ ...resendTarget, to: e.target.value })}
                  placeholder="recipient@example.com"
                  className="border-zinc-300 mt-1.5 font-mono-num"
                  data-testid="email-resend-to-input"
                  autoFocus
                />
                <p className="mt-1 text-2xs text-zinc-500">
                  Change this to send to a different address — same subject + body will be delivered.
                </p>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setResendTarget(null)} data-testid="email-resend-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={() => resendEmail(resendTarget.row, { to: resendTarget.to })}
                  disabled={resending === resendTarget.row._id || !resendTarget.to.includes("@")}
                  className="bg-zinc-950 hover:bg-zinc-800 text-white"
                  data-testid="email-resend-confirm"
                >
                  <Send className={"h-4 w-4 mr-1.5 " + (resending === resendTarget.row._id ? "animate-spin" : "")} />
                  {resendTarget.to !== resendTarget.row.to ? "Send to new address" : "Resend"}
                </Button>
              </div>
            </div>
          )}
        </_EmailDialogContent>
      </_EmailDialog>
    </Card>
  );
}

